import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { isMobile } from "@core/utility.service";
import { getFunctions, httpsCallable } from "firebase/functions";
import { BehaviorSubject, from, Observable, of } from "rxjs";
import { catchError, filter, tap, timeout } from "rxjs/operators";
import { ChatbotPopupComponent } from "../components/chatbot-popup/chatbot-popup.component";
import { AccountRepositoryService } from "../pages/account/account-repository.service";
import { RequestBodyService } from "./request-body.service";
@Injectable({
  providedIn: "root",
})
export class ChatbotService {
  user$ = this.authService.userDetails$;
  helpContent$ = this.accountService.helpAndFaq();
  helpSupport = true;
  selectedLanguage: string = "en";
  public reloadSource = new BehaviorSubject<boolean>(false);
  public currentReload: Observable<boolean> = this.reloadSource.asObservable(); // Observable for reload
  public callableFunction: any;
  constructor(
    private authService: AuthService,
    private accountService: AccountRepositoryService,
    private router: Router,
    private matDialog: MatDialog,
    private http: HttpClient,
    public requestBodyService: RequestBodyService
  ) {
    const functions = getFunctions();
    functions.region = "europe-west3";
    this.callableFunction = httpsCallable(functions, "apiv2");
  }
  // Observable for reload
  public changeReload(reload: boolean): void {
    this.reloadSource.next(reload);
  }

  chatBotSessionInit() {
    this.authService.resetUserRedirect(""); // This method is used to reset the user redirect node empty.
    this.user$.pipe(filter((f) => location.href.search("helpSupport") > -1)).subscribe(
      async (u: Data) => {
        if (u && u?.redirect) {
          const minusOne = -1;
          if (u?.redirect.search("live_chat") > -1) {
            window["BE_API"].closeChatWindow();
            const modal = this.matDialog.open(ChatbotPopupComponent, {
              height: "50%",
            });
            modal.afterClosed().subscribe(
              (data) => {
                if (data) {
                  this.router.navigateByUrl("mainTabs/tabs/account/livechat");
                  this.authService.resetUserRedirect("");
                }
              },
              (err) => {
                return err;
              }
            );
          } else if (u?.redirect.search("store") > minusOne) {
            this.router.navigateByUrl("/mainTabs/tabs/store");
          } else if (u?.redirect.search("voucher") > minusOne) {
            this.router.navigateByUrl("mainTabs/tabs/voucher");
          } else if (u?.redirect.search("syndicate") > minusOne) {
            this.router.navigateByUrl("mainTabs/tabs/syndicate");
          } else if (u?.redirect.search("account") > minusOne) {
            this.router.navigateByUrl("mainTabs/tabs/account");
          } else if (u?.redirect.search("game") > minusOne) {
            this.router.navigateByUrl("mainTabs/tabs/game");
          } else if (u?.redirect?.search("mgm") > minusOne) {
            this.router.navigateByUrl("refer-friends");
          }
          if (!isMobile.Android()) {
            window["BE_API"].onChatWindowClose = function () {
              window["BE_API"].destroy();
            };
          }
          return;
        }
      },
      (err) => {
        return err;
      }
    );
  }
  chatBotUrl(url: string) {
    return this.http.get<string>(url);
  }
  
  getUserDataforChatlio(userId): Observable<Data> {
    // Validate userId is provided
    if (!userId) {
      this.authService.errorHandlerLogs('getUserDataforChatlio: userId is null or undefined');
      return of(null);
    }
  
    // Validate request body can be created
    const body = this.requestBodyService.getDataforChatlio(userId);
    if (!body) {
      this.authService.errorHandlerLogs('getUserDataforChatlio: request body is null or undefined');
      return of(null);
    }
  
    // Validate callable function exists
    if (!this.callableFunction) {
      this.authService.errorHandlerLogs('getUserDataforChatlio: callable function is not initialized');
      return of(null);
    }
  
    const callableRequest = this.callableFunction(body);
  
    // Return the cloud function response immediately without delay
    // Validate authService and its method exist
    if (!this.authService || !this.authService.v2CloudFunctionResponse) {
      this.authService.errorHandlerLogs('getUserDataforChatlio: authService or v2CloudFunctionResponse is not available');
      return of(null);
    }
    
    return from(this.authService.v2CloudFunctionResponse(callableRequest)).pipe(
      timeout(10000), // Increased timeout to 10 seconds for cloud function call
      tap(response => {
        if (!response || Object.keys(response)?.length === 0) {
          this.authService.errorHandlerLogs('getUserDataforChatlio: Empty response from cloud function');
        }
      }),
      catchError(error => {
        if (error?.name === 'TimeoutError') {
          this.authService.errorHandlerLogs('getUserDataforChatlio: Cloud Function request timed out');
        } else {
          this.authService.errorHandlerLogs('getUserDataforChatlio: Error from cloud function response:', error);
        }
        return of(null);
      })
    );
  }
  
  


  getChatDetails(): Observable<Data> {
    return this.http.get<Data>('/api/chatbot/chats');
  }

  getChatDetailsByChatId(chatId: string): Observable<Data> {
    return this.http.get<Data>(`/api/chatbot/chats/${chatId}`);
  }
}
